import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Search, Users, CalendarDays, Newspaper, Gamepad2, HelpCircle } from 'lucide-react';
import { searchAPI } from '../../services/api';

const GROUPS = [
  { key: 'members', label: 'Membres', icon: Users },
  { key: 'events', label: 'Événements', icon: CalendarDays },
  { key: 'news', label: 'Actualités', icon: Newspaper },
  { key: 'games', label: 'Ludothèque', icon: Gamepad2 },
  { key: 'faq', label: 'FAQ', icon: HelpCircle },
];

const STATUS_LABELS = { roliste: 'Rôliste', mj: 'Meneur de jeu', both: 'Rôliste & Meneur' };

const ResultItem = ({ to, title, subtitle, badge, testId }) => (
  <Link to={to} data-testid={testId}
    className="block p-3 rounded-lg border border-purple-500/20 bg-black/40 hover:bg-purple-900/30 hover:border-purple-400/50 transition-colors">
    <div className="flex items-start justify-between gap-3">
      <span className="font-semibold text-purple-200 break-words">{title}</span>
      {badge && <Badge className="bg-purple-800/60 text-purple-100 border-purple-500/40 flex-shrink-0 text-xs">{badge}</Badge>}
    </div>
    {subtitle && <p className="text-xs sm:text-sm text-gray-400 mt-1 break-words">{subtitle}</p>}
  </Link>
);

const renderItem = (group, item, index) => {
  const testId = `search-result-${group}-${index}`;
  switch (group) {
    case 'members':
      return <ResultItem key={item.user_id} to={item.link} title={item.name} badge={STATUS_LABELS[item.player_status]} testId={testId} />;
    case 'events':
      return <ResultItem key={item.event_id} to={item.link} title={item.title} subtitle={item.excerpt} badge={item.date ? `${item.date}${item.time ? ` · ${item.time}` : ''}` : null} testId={testId} />;
    case 'news':
      return <ResultItem key={item.news_id} to={item.link} title={item.title} subtitle={item.excerpt} testId={testId} />;
    case 'games':
      return <ResultItem key={item.game_id || item.name} to={item.link} title={item.name} subtitle={item.excerpt} badge={item.themes?.[0]} testId={testId} />;
    case 'faq':
      return <ResultItem key={item.item_id} to={item.link} title={item.question} subtitle={item.excerpt} testId={testId} />;
    default:
      return null;
  }
};

const SearchTab = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        setResults(await searchAPI.search(trimmed));
      } catch (e) {
        console.error('Search error:', e);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="space-y-6" data-testid="search-tab">
      <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400" />
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} data-testid="global-search-input"
            placeholder="Rechercher un membre, un événement, une actualité, un jeu, une question FAQ..."
            className="pl-10 h-12 bg-black/50 border-purple-500/30 text-white text-base" />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {loading ? 'Recherche en cours...' : results ? `${results.total} résultat${results.total > 1 ? 's' : ''} pour « ${results.query} »` : 'Saisissez au moins 2 caractères'}
        </p>
      </Card>

      {results && results.total === 0 && (
        <Card className="bg-black/40 border border-purple-500/20 p-8 text-center text-gray-400" data-testid="search-no-results">
          Aucun résultat. Essayez un autre mot-clé.
        </Card>
      )}

      {results && results.total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {GROUPS.filter((g) => results[g.key]?.length > 0).map(({ key, label, icon: Icon }) => (
            <Card key={key} className="bg-gradient-to-br from-purple-900/30 to-black border border-purple-500/30 p-4 space-y-3" data-testid={`search-group-${key}`}>
              <h3 className="flex items-center gap-2 text-purple-300 font-bold">
                <Icon className="h-4 w-4" /> {label}
                <span className="text-xs text-gray-500 font-normal">({results[key].length})</span>
              </h3>
              <div className="space-y-2">{results[key].map((item, i) => renderItem(key, item, i))}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchTab;
